import { useCallback, useRef, useState } from 'react';
import { potEmptyMessage, withAgreementRejection } from '../utils/invoiceRejectionMessages';
import type { RejectionRecorder } from '../types/agreements.types';
import type { FundingChoiceState } from './useFundingChoice';
import type { InvoiceSubmission } from './useInvoiceSubmission';

interface Notice {
    agreementId: string;
    message: string;
}

export interface PotEmptyNotice {
    begin: () => RejectionRecorder;
    clearingChoice: (choice: FundingChoiceState) => FundingChoiceState;
    withSubmission: (submission: InvoiceSubmission) => InvoiceSubmission;
}

/** A pot_empty is about the agreement, not the approval, so it outlives the purpose scope; an explicit
 *  funding choice or a submit clears it and voids any request begun before it. */
export function usePotEmptyNotice(agreementId: string | undefined): PotEmptyNotice {
    const [notice, setNotice] = useState<Notice | null>(null);
    const generation = useRef(0);
    const message = notice !== null && notice.agreementId === agreementId ? notice.message : null;

    const begin = useCallback((): RejectionRecorder => {
        const started = generation.current;
        return (requestedFor, reasons) => {
            const recorded = potEmptyMessage(reasons);
            if (recorded !== null && started === generation.current) {
                setNotice({ agreementId: requestedFor, message: recorded });
            }
        };
    }, []);

    const clear = () => {
        generation.current += 1;
        setNotice(null);
    };

    return {
        begin,
        clearingChoice: (choice) => ({
            ...choice,
            choose: (next) => {
                clear();
                choice.choose(next);
            },
        }),
        withSubmission: (submission) => ({
            ...submission,
            reasonsByTarget: withAgreementRejection(submission.reasonsByTarget, message),
            submit: (values) => {
                clear();
                return submission.submit(values);
            },
        }),
    };
}
